import React from 'react';
import { LoadingPage } from 'ensightful-koala-ui';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useChat } from 'contexts/chat';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import courseService from 'services/course';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Icon,
    IconButton,
    PanelButton,
    Stack,
    Tag,
    Text,
    TextInput,
} from 'shell/components';
import { getPrettyTimestamp, getTooltipComponent, getUserPreferredFullName, splitArrayByCondition } from 'utils/helper';

const ControlPanelSection = ({
    activeGroupId,
    setActiveGroupId,
    channels,
    directs,
    latestMessages,
    activeChatId,
    setActiveChatId,
    setCreateChannelDialog,
    search,
    setSearch,
}) => {
    const { auth } = useAuth();
    const { data, setData } = useChat();
    const { groups, accountUsers } = useUser2();
    const { device } = useViewport();

    const markAllAsRead = async () => {
        await courseService.readAllChannels(auth.course_id);
        setData((current) => {
            return current.map((group) => {
                return {
                    ...group,
                    result: group.result.map((channel) => ({ ...channel, unread: false })),
                    unread_channels: 0,
                    unread_directs: 0,
                };
            });
        });
    };

    if (!activeGroupId) {
        if (auth.role === 'student') return <LoadingPage style={{ width: '100%' }} />;

        const { true: withUnread, false: withoutUnread } = splitArrayByCondition(
            data.filter(({ group_id }) => {
                const group = groups.find(({ id }) => id === group_id);
                if (group?.name?.toLowerCase().includes(search.toLowerCase())) return true;
                return false;
            }),
            ({ unread_channels, unread_directs }) => unread_channels > 0 || unread_directs > 0
        );

        return (
            <RootBox device={device}>
                <Stack spacing={4}>
                    <TextInput
                        disablePointerEventsOnIcon={!Boolean(search)}
                        endIcon={
                            search ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => setSearch('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Stack spacing={4}>
                        <Stack spacing={2}>
                            {withUnread.length ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text variant={'sh2'} weight={'semibold'}>
                                        Unread
                                    </Text>
                                    <Button variant={'tertiary'} onClick={markAllAsRead}>
                                        Mark All as Read
                                    </Button>
                                </Stack>
                            ) : null}
                            <Stack divider={<Divider />}>
                                {withUnread.map(({ group_id, unread_channels, unread_directs }) => {
                                    const group = groups.find(({ id }) => id === group_id);
                                    return (
                                        <PanelButton key={group_id} onClick={() => setActiveGroupId(group_id)}>
                                            <Stack
                                                alignItems={'center'}
                                                direction={'row'}
                                                justifyContent={'space-between'}
                                            >
                                                <Text data-cy={group.name} align={'left'} variant={'p1'}>
                                                    {group.name}
                                                </Text>
                                                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                                    {unread_channels ? (
                                                        <Badge
                                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                            color={'primary'}
                                                            overlap={'circular'}
                                                            variant={'dot'}
                                                        >
                                                            <Icon
                                                                sx={{ fontSize: '1rem' }}
                                                                kind={'messageRectangular'}
                                                            />
                                                        </Badge>
                                                    ) : null}
                                                    {unread_directs ? (
                                                        <Badge
                                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                            color={'primary'}
                                                            overlap={'circular'}
                                                            variant={'dot'}
                                                        >
                                                            <Icon sx={{ fontSize: '1rem' }} kind={'message'} />
                                                        </Badge>
                                                    ) : null}
                                                </Stack>
                                            </Stack>
                                        </PanelButton>
                                    );
                                })}
                            </Stack>
                        </Stack>
                        {withoutUnread.length ? (
                            <Stack spacing={2}>
                                <Text variant={'sh2'} weight={'semibold'}>
                                    All
                                </Text>
                                <Stack divider={<Divider />}>
                                    {withoutUnread.map(({ group_id }) => {
                                        const group = groups.find(({ id }) => id === group_id);
                                        return (
                                            <PanelButton key={group_id} onClick={() => setActiveGroupId(group_id)}>
                                                <Stack
                                                    alignItems={'center'}
                                                    direction={'row'}
                                                    justifyContent={'space-between'}
                                                >
                                                    <Text data-cy={group.name} align={'left'} variant={'p1'}>
                                                        {group.name}
                                                    </Text>
                                                </Stack>
                                            </PanelButton>
                                        );
                                    })}
                                </Stack>
                            </Stack>
                        ) : null}
                    </Stack>
                </Stack>
            </RootBox>
        );
    }

    const activeGroup = groups.find(({ id }) => id === activeGroupId);

    const renderChannels = () => {
        if (!channels) {
            return <LoadingPage style={{ height: 'fit-content' }} />;
        }

        return channels.map((channel) => {
            const groupData = data.find(({ group_id }) => group_id === activeGroupId);
            const isUnread = groupData.result.find(({ id }) => id === channel.id)?.unread;
            const latestMessage = latestMessages[channel.id];
            let latestMessageText = latestMessage?.message_text;
            if (latestMessage?.type === 'file') latestMessageText = latestMessageText || '[Attachment]';
            if (latestMessage?.type === 'photo') latestMessageText = latestMessageText || '[Image]';

            return (
                <PanelButton
                    key={channel.id}
                    selected={device === 'mobile' ? false : channel.id === activeChatId}
                    onClick={() => setActiveChatId(channel.id)}
                >
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomIcon kind={'channel'} />
                        <Stack sx={{ flex: 1, minWidth: 0 }} alignItems={'flex-start'} spacing={1}>
                            <Text
                                data-cy={channel.name}
                                align={'left'}
                                variant={'p2'}
                                weight={isUnread ? 'semibold' : 'regular'}
                            >
                                {channel.name}
                            </Text>
                            {latestMessage && (
                                <>
                                    <Text
                                        color={isUnread ? 'default' : 'light'}
                                        noWrap
                                        rich
                                        variant={'p2'}
                                        weight={isUnread ? 'semibold' : 'regular'}
                                    >
                                        {latestMessageText}
                                    </Text>
                                    <Text color={'light'} variant={'p4'}>
                                        {getPrettyTimestamp(latestMessage.created_on)}
                                    </Text>
                                </>
                            )}
                        </Stack>
                        {isUnread && <CustomBox />}
                    </Stack>
                </PanelButton>
            );
        });
    };

    const renderDirects = () => {
        if (!directs) {
            return <LoadingPage style={{ height: 'fit-content' }} />;
        }

        return directs
            .map((direct) => {
                const correspondentOwnerAccountId = direct.members.filter((id) => id !== auth.owner_account_id)[0];
                return {
                    direct,
                    correspondent: accountUsers.find(
                        ({ owner_account_id }) => owner_account_id === correspondentOwnerAccountId
                    ),
                };
            })
            .sort(({ correspondent: correspondentA }, { correspondent: correspondentB }) => {
                if (correspondentA.role === 'student' && correspondentB.role !== 'student') return -1;
                if (correspondentA.role !== 'student' && correspondentB.role === 'student') return 1;
                return 0;
            })
            .map(({ direct, correspondent }) => {
                const groupData = data.find(({ group_id }) => group_id === activeGroupId);
                const isUnread = groupData.result.find(({ id }) => id === direct.id)?.unread;
                const latestMessage = latestMessages[direct.id];

                return (
                    <PanelButton
                        key={direct.id}
                        selected={device === 'mobile' ? false : direct.id === activeChatId}
                        onClick={() => setActiveChatId(direct.id)}
                    >
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Avatar size={'medium'} user={correspondent} />
                            <Stack sx={{ flex: 1, minWidth: 0 }} alignItems={'flex-start'} spacing={1}>
                                {correspondent.role === 'instructor' && <Tag type={'role'} value={'instructor'} />}
                                <Text align={'left'} variant={'p2'} weight={isUnread ? 'semibold' : 'regular'}>
                                    {getUserPreferredFullName(correspondent)}
                                </Text>
                                {latestMessage && (
                                    <>
                                        <Text
                                            color={isUnread ? 'default' : 'light'}
                                            noWrap
                                            rich
                                            variant={'p2'}
                                            weight={isUnread ? 'semibold' : 'regular'}
                                        >
                                            {latestMessage.message_text}
                                        </Text>
                                        <Text color={'light'} variant={'p4'}>
                                            {getPrettyTimestamp(latestMessage.created_on)}
                                        </Text>
                                    </>
                                )}
                            </Stack>
                            {isUnread && <CustomBox />}
                        </Stack>
                    </PanelButton>
                );
            });
    };

    return (
        <RootBox device={device}>
            <Stack spacing={4}>
                {auth.role === 'instructor' && (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'back'}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => setActiveGroupId('')}
                        />
                        <Text variant={'sh2'}>{activeGroup.name}</Text>
                    </Stack>
                )}
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        Channels
                    </Text>
                    <IconButton
                        data-cy={'add-message-channel-button'}
                        aria-label={'create new channel'}
                        kind={'add'}
                        size={'small'}
                        tooltip={{ component: 'Create a new channel' }}
                        variant={'circle'}
                        onClick={() => setCreateChannelDialog({ open: true })}
                    />
                </Stack>
                <Stack>{renderChannels()}</Stack>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        Direct
                    </Text>
                </Stack>
                <Stack divider={<Divider />}>{renderDirects()}</Stack>
            </Stack>
        </RootBox>
    );
};

const RootBox = styled(Box)(({ device, theme }) => {
    return {
        flexShrink: 0,
        maxHeight: 'calc(100vh - 64px - 20px)',
        padding: theme.spacing(4),
        overflowY: 'auto',
        ...(device === 'mobile' && {
            width: '100%',
        }),
        ...(device === 'tablet' && {
            width: 229,
        }),
        ...(device === 'desktop' && {
            width: 350,
        }),
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '4px',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.neutral[500],
        fontSize: '40px',
        color: theme.palette.ensightful.neutral[100],
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '50%',
        width: '8px',
        height: '8px',
        background: theme.palette.ensightful.primary[500],
    };
});

export default ControlPanelSection;
